import React from 'react';
import PropTypes from 'prop-types';
import { InlineRenderer } from './InlineRenderer';
import { BlockPropType } from './contentPropTypes';

// Heading tags the API is allowed to request. Content always renders beneath
// the page's h2, so h3 is the top level available. The allowlist is checked
// here rather than trusted from the payload, since block.level is used
// directly as a tag name
const ALLOWED_HEADING_TAGS = ['h3', 'h4', 'h5', 'h6'];
const DEFAULT_HEADING_TAG = 'h3';

/**
 * Renders block-level elements (headings, paragraphs, lists, line breaks)
 */
export const BlockRenderer = ({ block, removeBottomMargin = false }) => {
  if (!block || typeof block !== 'object') {
    return null;
  }

  switch (block.type) {
    case 'heading': {
      const HeadingTag = ALLOWED_HEADING_TAGS.includes(block.level)
        ? block.level
        : DEFAULT_HEADING_TAG;

      return (
        <HeadingTag>
          <InlineRenderer content={block.content} />
        </HeadingTag>
      );
    }
    case 'paragraph':
      return (
        <p
          className={removeBottomMargin ? 'vads-u-margin-bottom--0' : undefined}
        >
          <InlineRenderer content={block.content} />
        </p>
      );
    case 'list': {
      const ListTag = block.style === 'numbered' ? 'ol' : 'ul';
      const className = block.style === 'bullet' ? 'bullet-disc' : undefined;

      // Filter out null, undefined, empty strings, and empty arrays for accessibility
      const validItems = (block.items || []).filter(item => {
        if (item == null) return false; // Filters null and undefined
        if (typeof item === 'string') return item.trim().length > 0;
        if (Array.isArray(item)) return item.length > 0;
        return true; // Keep objects (schema guarantees they have required properties)
      });

      // Don't render empty lists
      if (validItems.length === 0) {
        return null;
      }

      return (
        <ListTag className={className}>
          {validItems.map((item, idx) => (
            <li key={idx}>
              <InlineRenderer content={item} />
            </li>
          ))}
        </ListTag>
      );
    }
    case 'lineBreak':
      return <br />;
    default:
      return null;
  }
};

BlockRenderer.propTypes = {
  block: BlockPropType.isRequired,
  removeBottomMargin: PropTypes.bool,
};
